import { Close } from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import useDialog from '../../../../hooks/useDialog';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

export default function DialogProductDetails({ product, brand, onCreateOffer, isSynced }) {
    const { open, handleClose } = useDialog('#product-details');
    const [tab, setTab] = useState('options');
    const [selectedImage, setSelectedImage] = useState(0);

    // console.log('product : ', product);
    // console.log('brand : ', brand);

    
    if (!open || !product) return null;

    const formatPrice = price => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(price);
    };

    const calculateCommission = (price, commission) => {
        if (!commission || !price) {
            return 0;
        }

        let result = 0;

        // FIXME: Alguns brands retornam commission.type undefined
        // Temporariamente tratando undefined como FIXED
        // TODO: Revisar com a Lomadee qual é a estrutura correta quando type for undefined
        const commissionType = commission.type || 'FIXED';

        if (commissionType === 'PERCENTAGE') {
            result = (price * commission.value) / 100;
        } else if (commissionType === 'FIXED' || !commission.type) {
            result = commission.value;
        }

        return result;
    };

    return (
        <Dialog open={open} fullWidth maxWidth="lg" onClose={handleClose}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <DialogTitle>{product.name}</DialogTitle>
                <IconButton size="small" onClick={handleClose} sx={{ mr: 2 }}>
                    <Close />
                </IconButton>
            </Stack>

            <Divider />

            <DialogContent sx={{ p: 0 }}>
                <TabContext value={tab}>
                    <TabList onChange={(_, value) => setTab(value)}>
                        <Tab label="Opções e Preços" value="options" />
                        <Tab label="Descrição" value="description" />
                        <Tab label="Imagens" value="images" />
                    </TabList>

                    <TabPanel value="options">
                        <Stack spacing={3}>
                            {/* Informações da Marca e Comissão */}
                            {brand?.commission && (
                                <Alert severity="success" icon="💰">
                                    <Typography variant="subtitle2" gutterBottom>
                                        Comissão da Marca: {brand.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        {brand.commission.type === 'PERCENTAGE'
                                            ? `${brand.commission.value}% do valor de cada venda`
                                            : `R$ ${brand.commission.value.toFixed(2)} fixo por venda${
                                                  !brand.commission.type ? ' (tipo indefinido - assumindo fixo)' : ''
                                              }`}
                                    </Typography>
                                    {!brand.commission.type && (
                                        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                                            ⚠️ FIXME: Tipo de comissão não especificado pela API - assumindo FIXED temporariamente
                                        </Typography>
                                    )}
                                </Alert>
                            )}

                            {/* Lista de Opções do Produto */}
                            {product.options?.map((option, optionIndex) => (
                                <Paper key={`option_${optionIndex}`} variant="outlined" sx={{ p: 2 }}>
                                    <Stack spacing={2}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="subtitle1">
                                                {option.name}
                                                {option.ean && <Chip size="small" label={`EAN: ${option.ean}`} sx={{ ml: 1 }} variant="outlined" />}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={option.available ? 'Disponível' : 'Indisponível'}
                                                color={option.available ? 'success' : 'error'}
                                            />
                                        </Stack>

                                        {option.seller && (
                                            <Typography variant="caption" color="text.secondary">
                                                Vendedor: {option.seller}
                                            </Typography>
                                        )}

                                        {/* Estoque */}
                                        {option.stocks && option.stocks.length > 0 && (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="caption" color="text.secondary">
                                                    Estoque:
                                                </Typography>
                                                {option.stocks.map((stock, stockIndex) => (
                                                    <Chip
                                                        key={`stock_${stockIndex}`}
                                                        size="small"
                                                        label={`${stock.value} unidades`}
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Stack>
                                        )}

                                        {/* Tabela de Preços */}
                                        {option.pricing && option.pricing.length > 0 && (
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Preço de Lista</TableCell>
                                                            <TableCell>Preço Final</TableCell>
                                                            <TableCell>Desconto</TableCell>
                                                            {brand?.commission && <TableCell>Comissão</TableCell>}
                                                            {brand?.commission && <TableCell>Cashback Máx.</TableCell>}
                                                            <TableCell align="right">Ações</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {option.pricing.map((pricing, pricingIndex) => {
                                                            
                                                            const discount =
                                                                pricing.listPrice > pricing.price
                                                                    ? ((pricing.listPrice - pricing.price) / pricing.listPrice) * 100
                                                                    : 0;
                                                            const commission = brand?.commission
                                                                ? calculateCommission(pricing.price, brand.commission)
                                                                : 0;

                                                            return (
                                                                <TableRow key={`pricing_${pricingIndex}`}>
                                                                    <TableCell>
                                                                        {pricing.listPrice > 0 ? formatPrice(pricing.listPrice) : '-'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                                                            {formatPrice(pricing.price)}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {discount > 0 ? (
                                                                            <Chip size="small" label={`${discount.toFixed(0)}% OFF`} color="error" />
                                                                        ) : (
                                                                            '-'
                                                                        )}
                                                                    </TableCell>
                                                                    {brand?.commission && (
                                                                        <TableCell>
                                                                            <Typography variant="body2" color="success.main" fontWeight="medium">
                                                                                {formatPrice(commission)}
                                                                            </Typography>
                                                                        </TableCell>
                                                                    )}
                                                                    {brand?.commission && (
                                                                        <TableCell>
                                                                            <Typography variant="caption" color="success.dark">
                                                                                Até {formatPrice(commission)}
                                                                            </Typography>
                                                                        </TableCell>
                                                                    )}
                                                                    <TableCell align="right">
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"

                                                                            onClick={() =>
                                                                                onCreateOffer({
                                                                                    product,
                                                                                    option,
                                                                                    pricing,
                                                                                    commission,
                                                                                    brand
                                                                                })
                                                                            }
                                                                            disabled={!option.available || isSynced  }
                                                                        >
                                                                         { isSynced ? 'Oferta já cadastrada' : 'Cadastrar oferta' }  

                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </TabPanel>

                    <TabPanel value="description">
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="body2" dangerouslySetInnerHTML={{ __html: product.description || 'Sem descrição disponível' }} />
                        </Paper>
                    </TabPanel>

                    <TabPanel value="images">
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                {product.images && product.images.length > 0 && (
                                    <Paper variant="outlined" sx={{ p: 1 }}>
                                        <img
                                            src={product.images[selectedImage]?.url}
                                            alt={product.name}
                                            style={{ width: '100%', height: 'auto', borderRadius: 8 }}
                                        />
                                    </Paper>
                                )}
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Stack spacing={1}>
                                    {product.images?.map((image, index) => (
                                        <Paper
                                            key={`image_${index}`}
                                            variant="outlined"
                                            sx={{
                                                p: 1,
                                                cursor: 'pointer',
                                                border: selectedImage === index ? '2px solid' : '1px solid',
                                                borderColor: selectedImage === index ? 'primary.main' : 'divider',
                                            }}
                                            onClick={() => setSelectedImage(index)}
                                        >
                                            <img
                                                src={image.url}
                                                alt={`${product.name} - ${index + 1}`}
                                                style={{ width: '100%', height: 'auto', borderRadius: 4 }}
                                            />
                                        </Paper>
                                    ))}
                                </Stack>
                            </Grid>
                        </Grid>
                    </TabPanel>
                </TabContext>
            </DialogContent>

            <Divider />

            <DialogActions>
                <Button onClick={handleClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}
